import { Stats, LevelUpResult } from '../types';

// Calculate XP required for next level (exponential growth)
export const calculateXPForLevel = (level: number): number => {
  const baseXP = 100;
  return Math.floor(baseXP * Math.pow(level, 1.5));
};

// Get title based on level (aligned with achievement system)
export const getTitleForLevel = (level: number): string => {
  if (level >= 100) return 'Shadow Monarch';
  if (level >= 75) return 'S-Rank Hunter';
  if (level >= 50) return 'A-Rank Hunter';
  if (level >= 30) return 'B-Rank Hunter';
  if (level >= 20) return 'C-Rank Hunter';
  if (level >= 10) return 'D-Rank Hunter';
  if (level >= 5) return 'E-Rank Hunter';
  return 'Civilian';
};

// Calculate stat increases on level up
export const calculateStatIncreases = (level: number): Partial<Stats> => {
  // Every level gives stat points
  const baseIncrease = 2;
  const bonusIncrease = Math.floor(level / 10); // Bonus every 10 levels
  
  return {
    strength: baseIncrease + bonusIncrease,
    intelligence: baseIncrease + bonusIncrease,
    agility: baseIncrease + bonusIncrease,
    vitality: baseIncrease + bonusIncrease,
    sense: baseIncrease + bonusIncrease,
  };
};

// Process XP gain and handle level ups
export const processXPGain = (
  currentLevel: number,
  currentXP: number,
  xpGained: number,
  currentStats: Stats
): { 
  newLevel: number; 
  newXP: number; 
  levelUpResults: LevelUpResult[];
  newStats: Stats;
} => {
  let level = currentLevel;
  let xp = currentXP + xpGained;
  let xpNeeded = calculateXPForLevel(level);
  const levelUpResults: LevelUpResult[] = [];
  let stats = { ...currentStats };

  // Handle multiple level ups
  while (xp >= xpNeeded) {
    xp -= xpNeeded;
    level += 1;
    
    const statIncreases = calculateStatIncreases(level);
    
    // Apply stat increases
    stats = {
      strength: stats.strength + (statIncreases.strength || 0),
      intelligence: stats.intelligence + (statIncreases.intelligence || 0),
      agility: stats.agility + (statIncreases.agility || 0),
      vitality: stats.vitality + (statIncreases.vitality || 0),
      sense: stats.sense + (statIncreases.sense || 0),
    };

    const newTitle = getTitleForLevel(level);
    
    levelUpResults.push({
      newLevel: level,
      statIncreases,
      newTitle,
    });

    xpNeeded = calculateXPForLevel(level);
  }

  return {
    newLevel: level,
    newXP: xp,
    levelUpResults,
    newStats: stats,
  };
};

// Get XP reward based on quest difficulty
export const getXPRewardForDifficulty = (difficulty: string): number => {
  switch (difficulty) {
    case 'easy':
      return 10;
    case 'normal':
      return 20;
    case 'hard':
      return 40;
    case 'boss':
      return 100;
    default:
      return 10;
  }
};

// Get currency reward based on quest difficulty
export const getCurrencyRewardForDifficulty = (difficulty: string): number => {
  switch (difficulty) {
    case 'easy':
      return 1;
    case 'normal':
      return 2;
    case 'hard':
      return 4;
    case 'boss':
      return 10;
    default:
      return 1;
  }
};

// Get stat rewards based on quest difficulty
export const getStatRewardsForDifficulty = (difficulty: string): Partial<Stats> => {
  switch (difficulty) {
    case 'easy':
      return { strength: 1 };
    case 'normal':
      return { intelligence: 1, sense: 1 };
    case 'hard':
      return { agility: 2, vitality: 2 };
    case 'boss':
      return { strength: 3, intelligence: 3 };
    default:
      return {};
  }
};

